import { useNavigate } from 'react-router'
import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs'
import Spinner from '@cloudscape-design/components/spinner'
import SpaceBetween from '@cloudscape-design/components/space-between'
import type { ContentStats } from '@/lib/types'
import AIAnnotation from './AIAnnotation'
import { InsightBullet } from '../../../lib/types'

interface Props {
  stats: ContentStats | null
  loading: boolean
  clientId: string | null
  insights?: InsightBullet[]
}

export default function ContentOverviewWidget({ stats, loading, clientId, insights }: Props) {
  const navigate = useNavigate()

  if (loading) {
    return (
      <Box textAlign="center" padding="xl">
        <Spinner size="large" nativeAttributes={{ 'aria-label': 'Loading' }} />
      </Box>
    )
  }
  if (!stats) {
    return (
      <Box textAlign="center" padding="l" color="text-body-secondary">
        No data available
      </Box>
    )
  }

  if (stats.totalGenerated === 0) {
    return (
      <Alert
        statusIconAriaLabel="Info"
        header="No content generated yet"
        action={
          clientId ? (
            <Button onClick={() => void navigate(`/workspace/clients/${clientId}/campaigns`)}>
              Start a campaign
            </Button>
          ) : undefined
        }
      >
        Run a campaign from the client workspace to start generating content.
      </Alert>
    )
  }

  const items = [
    { label: 'Generated', count: stats.totalGenerated, statusFilter: 'all' },
    { label: 'Pending Review', count: stats.totalPendingReview, statusFilter: 'pending' },
    { label: 'Published', count: stats.totalPublished, statusFilter: 'published' },
  ]

  return (
    <SpaceBetween size="s">
      <KeyValuePairs
        columns={3}
        items={items.map(item => ({
          label: item.label,
          value: (
            <Button
              variant="inline-link"
              onClick={() => {
                if (!clientId) return
                void navigate(`/workspace/clients/${clientId}/queue`, {
                  state: { statusFilter: item.statusFilter },
                })
              }}
            >
              {item.count}
            </Button>
          ),
        }))}
      />
      <AIAnnotation bullets={insights ?? []} />
    </SpaceBetween>
  )
}
